package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.BusyElsewhere
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.util.gapMinutesTo
import com.evoapps.evoshift.domain.util.overlaps

/** SH-135: how a shift here clashes with the person's work at another workplace. */
enum class ElsewhereIssue {
    /** It overlaps a shift there: the person cannot be in two places. */
    OVERLAP,

    /**
     * Less than 11 hours between it and a shift there. A company rule, not the labour code's:
     * each employer counts working time separately, but the person rests only once.
     */
    SHORT_REST
}

/**
 * SH-135: the roster builder's check of this workplace's shifts against the people's shifts
 * elsewhere ([BusyElsewhere], from the server). AutoScheduler applies the same rules to its
 * context shifts (BUSY_ELSEWHERE, rest), so what it plans is never flagged here.
 */
object BusyElsewhereCheck {
    /** The rest kept around work elsewhere, as auto-assign keeps it (11 hours). */
    const val REST_MINUTES = LabourCode.DAILY_REST_HOURS * 60L

    /** A shift here and the time elsewhere it clashes with. */
    data class Clash(val shift: Shift, val other: BusyElsewhere, val issue: ElsewhereIssue)

    /**
     * SH-136: each of [shifts] that clashes with its person's [busy] time, with the time it clashes
     * with (an overlap wins; then the nearest in time), for the roster's warnings (RosterIssues).
     */
    fun clashes(shifts: List<Shift>, busy: List<BusyElsewhere>): List<Clash> {
        if (busy.isEmpty()) return emptyList()
        val byUser = busy.groupBy { it.userId }
        return shifts.mapNotNull { shift ->
            val theirs = byUser[shift.userId ?: return@mapNotNull null] ?: return@mapNotNull null
            theirs.mapNotNull { other ->
                when {
                    shift.plannedTime.overlaps(other.plannedTime) -> Clash(shift, other, ElsewhereIssue.OVERLAP)
                    shortRest(shift, other) -> Clash(shift, other, ElsewhereIssue.SHORT_REST)
                    else -> null
                }
            }.minWithOrNull(compareBy<Clash> { it.issue.ordinal }.thenBy { it.other.plannedTime.start })
        }
    }

    /** Each of [shifts] that clashes with its person's [busy] time, by shift id (an overlap wins). */
    fun check(shifts: List<Shift>, busy: List<BusyElsewhere>): Map<String, ElsewhereIssue> =
        clashes(shifts, busy).associate { it.shift.id to it.issue }

    private fun shortRest(shift: Shift, other: BusyElsewhere): Boolean = runCatching {
        val gap = if (shift.plannedTime.start >= other.plannedTime.end) other.plannedTime.gapMinutesTo(shift.plannedTime)
        else shift.plannedTime.gapMinutesTo(other.plannedTime)
        gap in 0 until REST_MINUTES
    }.getOrDefault(false)
}
